/**
 * PlaceDetail — /places/:slug
 *
 * Dynamic drill-down. Works at every level (country / state / county /
 * city / village) because the API returns the same envelope shape.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, MapPin, MessageCircle, ArrowRight, Users, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import FollowButton from "@/components/FollowButton";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/geo`;
const CP_API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/chatpulse/v1`;

const KIND_LABEL = {
  country: "Country", state: "State / Province",
  county: "County / District", city: "City", village: "Village", other: "Region",
};

const KIND_ORDER = ["state", "county", "city", "village", "other"];

export default function PlaceDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [payload, setPayload] = useState(null);
  const [threads, setThreads] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setPayload(null);
    setNotFound(false);
    (async () => {
      const r = await fetch(`${API}/places/${slug}`);
      if (r.status === 404) { setNotFound(true); return; }
      const data = await r.json();
      setPayload(data);
      const params = new URLSearchParams();
      const p = data.place || {};
      if (p.kind === "country")  params.set("country", p.name);
      if (p.kind === "state")    params.set("state",   p.name);
      if (p.kind === "city")     params.set("city",    p.name);
      params.set("limit", "10");
      const t = await fetch(`${CP_API}/timeline?${params}`).then(r => r.json());
      setThreads(t.threads || []);
    })();
  }, [slug, reload]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="place-not-found">
        <MapPin className="w-8 h-8 text-slate-300 mx-auto" />
        <p className="mt-3 text-sm text-slate-600">We don't have this place yet.</p>
        <Link to="/places" className="text-blue-600 hover:underline text-sm font-semibold mt-2 inline-block">Browse all places →</Link>
      </div>
    );
  }
  if (!payload) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-sm text-slate-400" data-testid="place-loading">Loading…</div>;

  const { place, breadcrumb, children } = payload;
  const kinds = KIND_ORDER.filter((k) => children[k] && children[k].length);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6" data-testid="place-detail">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-1 text-xs text-slate-500 mb-4" data-testid="place-breadcrumb">
        <Link to="/places" className="hover:text-blue-700 font-semibold uppercase tracking-widest">World</Link>
        {breadcrumb.slice(0, -1).map((b) => (
          <span key={b.slug} className="inline-flex items-center gap-1">
            <ChevronRight className="w-3 h-3 text-slate-300" />
            <Link to={`/places/${b.slug}`} className="hover:text-blue-700">{b.name}</Link>
          </span>
        ))}
        <ChevronRight className="w-3 h-3 text-slate-300" />
        <span className="text-slate-900 font-semibold">{place.name}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-slate-400">
            <MapPin className="w-3 h-3" /> {KIND_LABEL[place.kind] || place.kind}
            {place.subdivision_type && place.subdivision_type !== KIND_LABEL[place.kind] && (
              <span className="ml-1 text-slate-400">· {place.subdivision_type}</span>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-1">{place.name}</h1>
          <p className="text-sm text-slate-600 mt-1">
            {place.discussions_count || 0} discussions · Part of the SocialPulse conversation network
          </p>
        </div>
        <Link
          to={`/thread/new?scope=near&country=${encodeURIComponent(place.kind === "country" ? place.name : "")}&state=${encodeURIComponent(place.kind === "state" ? place.name : "")}&city=${encodeURIComponent(place.kind === "city" ? place.name : "")}`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
          data-testid="place-discuss-btn"
        >
          <MessageCircle className="w-3.5 h-3.5" /> Discuss this place
        </Link>
      </div>

      <div className="mt-3">
        <FollowButton targetType="place" targetSlug={place.slug} />
      </div>

      {/* Children by kind */}
      {kinds.map((k) => (
        <section key={k} className="mt-8" data-testid={`children-${k}`}>
          <h2 className="font-bold text-slate-900 mb-3 inline-flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-600" />
            {plural(KIND_LABEL[k] || k)}
            <span className="text-[11px] font-normal text-slate-400">· {children[k].length}</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
            {children[k].map((c) => (
              <Link
                key={c.slug}
                to={`/places/${c.slug}`}
                data-testid={`child-${c.slug}`}
                className="rounded-lg border border-slate-200 bg-white p-3 hover:border-blue-400 hover:shadow-sm transition"
              >
                <div className="text-sm font-semibold text-slate-900 line-clamp-1">{c.name}</div>
                {c.subdivision_type && (
                  <div className="text-[10px] text-slate-400 mt-0.5">{c.subdivision_type}</div>
                )}
                <div className="text-[11px] text-blue-600 mt-0.5 inline-flex items-center gap-1">
                  <Users className="w-3 h-3" /> {c.discussions_count || 0}
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}

      {/* Add a city / village (user-generated, auth-gated) */}
      {["country", "state", "county", "city"].includes(place.kind) && (
        <AddChildPlace
          parent={place}
          user={user}
          onAdded={() => setReload((n) => n + 1)}
        />
      )}

      {/* Discussions here */}
      <section className="mt-10" data-testid="place-threads">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-bold text-slate-900 text-lg inline-flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-blue-600" /> Discussions
          </h2>
          <Link to={`/thread/new`} className="text-xs font-semibold text-blue-600 hover:underline">
            Start a discussion <ArrowRight className="inline w-3 h-3" />
          </Link>
        </div>
        {threads.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center bg-slate-50">
            <p className="text-sm text-slate-600">No discussions here yet.</p>
            <p className="text-xs text-slate-500 mt-1">Be the first voice — start a thread about {place.name}.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {threads.map((t) => (
              <li key={t.id}>
                <Link
                  to={`/thread/${t.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm transition"
                  data-testid={`place-thread-${t.id}`}
                >
                  <MessageCircle className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="flex-1 text-sm font-semibold text-slate-900 line-clamp-1">{t.title}</span>
                  <span className="text-[11px] text-slate-500 shrink-0">{t.post_count || 0} replies</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function plural(label) {
  if (!label) return "Regions";
  if (/y$/.test(label)) return label.replace(/y$/, "ies");
  return label.endsWith("s") ? label : `${label}s`;
}

function AddChildPlace({ parent, user, onAdded }) {
  const [open, setOpen]   = useState(false);
  const [name, setName]   = useState("");
  const [kind, setKind]   = useState(parent.kind === "city" ? "village" : "city");
  const [busy, setBusy]   = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to add places."); return; }
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/places`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: name.trim(), kind, parent_slug: parent.slug }),
      });
      if (!r.ok) {
        const msg = (await r.json()).detail || r.statusText;
        throw new Error(msg);
      }
      toast.success(`${kind === "city" ? "City" : "Village"} added — it's now discoverable.`);
      setName(""); setOpen(false);
      onAdded && onAdded();
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-8 rounded-xl border border-dashed border-slate-300 p-4 bg-slate-50" data-testid="add-child-place">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-900"
          data-testid="add-child-open"
        >
          <Plus className="w-4 h-4" /> Add a {parent.kind === "city" ? "village" : "city or village"} in {parent.name}
        </button>
      ) : (
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={parent.kind === "city" ? "Village name" : "City or village name"}
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400 bg-white"
            data-testid="add-child-name"
            required minLength={2} maxLength={100}
          />
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            disabled={parent.kind === "city"}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400 bg-white"
            data-testid="add-child-kind"
          >
            <option value="city">City</option>
            <option value="village">Village</option>
          </select>
          <button
            type="submit"
            disabled={busy || name.trim().length < 2}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50"
            data-testid="add-child-submit"
          >
            <Plus className="w-3 h-3" /> {busy ? "Adding…" : "Add place"}
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); setName(""); }}
            className="text-xs text-slate-500 hover:text-slate-800 px-2"
            data-testid="add-child-cancel"
          >
            Cancel
          </button>
        </form>
      )}
      {!user && open && (
        <p className="mt-2 text-xs text-slate-500">
          <Link to="/auth/socialpulse-login" className="text-blue-600 hover:underline">Sign in with SocialPulse</Link> to add places.
        </p>
      )}
    </section>
  );
}
